// Client-side stem separation for the Studio — runs htdemucs_6s in the browser
// via onnxruntime-web + WebGPU, in a Web Worker (off the main thread, no jank).
// Mirrors the extension's client-side path but uses a worker (a normal web app,
// unlike a content script, can spawn same-origin workers freely).
//
// Returns blob: URLs keyed by stem name — the SAME shape StemEngine.load() and
// loadStems() already consume, so the mixer is unchanged.
import { STEMS, type StemName } from './studioEngine';
import { fetchEngineBytes, scheduleEnginePrefetch, engineCached, isModelParseError, dropEngineBytes } from './modelCache';
import { detectSolos, type SoloRegion } from './soloDetect';

/**
 * The worker production actually runs.
 *
 * This used to say `sep-worker.js`, and the deployed bundle was hand-patched to
 * point here instead — a patch that lived only in the shipped file, never in the
 * repo, so every rebuild silently reverted live separations to the older worker.
 * Naming it in source removes that trap: builds are now reproducible and the
 * patch step is unnecessary. `sep-worker.js` is still on the server and is the
 * older build; do not point back at it without checking why.
 */
const WORKER_URL = '/vendor/sep-worker-m1.js';

export type SepEngine = 'demucs' | 'roformer';

/**
 * What the worker actually dispatches on. `mdx` is the 2-stem mobile tier — it
 * is never user-selectable, only reachable through the __RL_ENGINE__ override
 * below, so it stays out of SepEngine and off the quality picker.
 */
type WorkerEngine = SepEngine | 'mdx';

/**
 * Mobile-tier overrides, published by /vendor/mobile-tier.js before this bundle
 * loads. That script detects phones and routes them to a 64MB 2-stem model:
 * htdemucs_6s is a 143MB download whose inference peak blows past the ~1-1.5GB
 * a mobile tab gets, so the tab is killed and the user sees "Something went
 * wrong". On desktop the script returns early and sets nothing, so both reads
 * fall through to the values in this file and behaviour is unchanged.
 *
 * These two reads used to be hand-patched into the built bundle and were lost
 * without error on every rebuild — three times. They live in source now so
 * builds carry them; see backups/studio-mobile-tier-20260812-0205.
 */
declare global {
  interface Window {
    __RL_VM__?: Record<SepEngine, { modelUrl: string }>;
    __RL_ENGINE__?: WorkerEngine;
  }
}

// Two switchable models, same 6 stems. demucs = the original htdemucs_6s;
// roformer = BS-RoFormer-SW (much cleaner stems, esp. piano; bigger download;
// weights provenance unknown — experimental, owner-gated).
const ENGINES: Record<SepEngine, { modelUrl: string }> =
  (typeof window !== 'undefined' && window.__RL_VM__) || {
    demucs: { modelUrl: '/vendor/htdemucs_6s.onnx' },
    roformer: { modelUrl: '/vendor/bs_roformer_sw_6stem_fp16.onnx' },
  };

let worker: Worker | null = null;
let workerEngine: SepEngine | null = null;
let readyPromise: Promise<void> | null = null;

export type SepProgress = (frac: number, label?: string) => void;

// Warm-up progress is decoupled from any one caller: preloadEngine() starts
// the download with nobody listening, and a separation that begins mid-warm
// attaches here and immediately receives the latest state — so its progress
// bar picks up wherever the background warm-up already got to.
let extProgress: SepProgress | null = null;
let lastFrac = 0;
let lastLabel: string | undefined;
function notify(frac: number, label?: string) {
  lastFrac = frac;
  lastLabel = label;
  extProgress?.(frac, label);
}
function attachProgress(cb: SepProgress | null) {
  extProgress = cb;
  if (cb && lastFrac > 0) cb(lastFrac, lastLabel);
}

/**
 * Fire-and-forget engine warm-up (download weights + build the GPU session).
 * Called on intent signals — capture starting, a track decoded — so the
 * one-time setup runs while the user is already waiting on something else.
 */
export function preloadEngine(engine: SepEngine = 'demucs'): void {
  ensureReady(engine).catch(() => {});
}

/**
 * Awaitable engine warm-up with progress — the same work `preloadEngine` does
 * fire-and-forget, but the caller can wait for it and show the download.
 *
 * Exists so a separation can get the engine READY BEFORE it spends the user's
 * quota: the weights are ~142MB on a first visit, and charging ahead of that
 * download meant an abandoned or failed fetch still cost a separation (measured
 * at ~30% of first downloads). `ensureReady` memoises `readyPromise`, so the
 * `separateClientSide` call that follows resolves instantly rather than
 * re-fetching.
 */
export async function ensureEngineReady(engine: SepEngine = 'demucs', onProgress?: SepProgress): Promise<void> {
  attachProgress(onProgress ?? null);
  try {
    await ensureReady(engine);
  } finally {
    attachProgress(null);
  }
}

/** True when this engine's weights are already local, i.e. no setup wait. */
export async function engineIsCached(engine: SepEngine = 'demucs'): Promise<boolean> {
  return engineCached(ENGINES[engine].modelUrl);
}

/**
 * Polite idle prefetch of the Standard engine into durable Cache Storage,
 * plus HTTP-cache warms for the runtime files the worker fetches itself.
 * Call once when the Studio mounts.
 */
export function prefetchStandardEngine(): void {
  scheduleEnginePrefetch(
    [ENGINES.demucs.modelUrl],
    ['/vendor/ort-wasm-simd-threaded.jsep.wasm', '/vendor/ort-wasm-simd-threaded.jsep.mjs'],
  );
}

function wavBlob(left: Float32Array, right: Float32Array, sr: number): Blob {
  const n = left.length;
  const dataLen = n * 4; // 16-bit stereo
  const ab = new ArrayBuffer(44 + dataLen);
  const v = new DataView(ab);
  const ws = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  ws(0, 'RIFF'); v.setUint32(4, 36 + dataLen, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 2, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * 4, true); v.setUint16(32, 4, true);
  v.setUint16(34, 16, true); ws(36, 'data'); v.setUint32(40, dataLen, true);
  let o = 44;
  for (let i = 0; i < n; i++) {
    v.setInt16(o, Math.max(-1, Math.min(1, left[i])) * 0x7fff, true); o += 2;
    v.setInt16(o, Math.max(-1, Math.min(1, right[i])) * 0x7fff, true); o += 2;
  }
  return new Blob([ab], { type: 'audio/wav' });
}

function rms(a: Float32Array): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += a[i] * a[i];
  return Math.sqrt(s / Math.max(1, a.length));
}

async function ensureReady(engine: SepEngine): Promise<void> {
  if (readyPromise && workerEngine === engine) return readyPromise;
  // Engine switch (or first run): drop the old worker/session, start fresh.
  if (worker) { worker.terminate(); worker = null; }
  workerEngine = engine;
  readyPromise = (async () => {
    const modelUrl = ENGINES[engine].modelUrl;
    notify(0.05, 'Loading engine');
    // Durable-cache first (Cache Storage, survives HTTP-cache eviction) —
    // populated by a previous run or the Studio's idle prefetch.
    // Resumable: fetched in 8 MB ranges that are cached as they land, so an
    // interrupted download resumes on the next visit instead of restarting.
    // Returns the durable copy immediately when there already is one.
    const u8 = await fetchEngineBytes(modelUrl, (_frac, received, total) => {
      notify(
        0.05 + 0.55 * (total ? received / total : 0),
        total ? `Downloading engine ${(received / 1e6) | 0}/${(total / 1e6) | 0} MB` : 'Downloading engine',
      );
    });
    notify(0.62, 'Starting engine');
    const w = new Worker(WORKER_URL);
    worker = w;
    await new Promise<void>((resolve, reject) => {
      const on = (e: MessageEvent) => {
        if (e.data.type === 'ready') { w.removeEventListener('message', on); resolve(); }
        else if (e.data.type === 'error') {
          w.removeEventListener('message', on);
          const err = new Error(e.data.error);
          // Weights ORT can't parse fail every later run too while they sit in
          // the cache: drop them first, so the next attempt downloads afresh.
          if (isModelParseError(String(e.data.error))) void dropEngineBytes(modelUrl).then(() => reject(err));
          else reject(err);
        }
      };
      w.addEventListener('message', on);
      w.addEventListener('error', (e) => reject(new Error('worker error: ' + ((e as ErrorEvent).message || 'load failed'))));
      // The worker picks its code path from this field alone — it never reads
      // the model URL — so the override has to travel with the model map above.
      // A 2-stem model handed to the htdemucs path is a tensor-shape error, not
      // a fix, which is why these two reads are useless apart.
      const initEngine: WorkerEngine = (typeof window !== 'undefined' && window.__RL_ENGINE__) || engine;
      w.postMessage({ type: 'init', engine: initEngine, weights: u8.buffer, eps: ['webgpu', 'wasm'] }, [u8.buffer]);
    });
    notify(0.7, 'Ready');
  })();
  try {
    await readyPromise;
  } catch (e) {
    // Don't poison future attempts with a rejected cached promise.
    readyPromise = null;
    workerEngine = null;
    throw e;
  }
  return readyPromise;
}

export interface ClientSepResult {
  stemUrls: Record<StemName, string>;
  emptyStems: Set<StemName>;
  /** Auto-detected solo/section regions (Guitar solo, Drum solo, …), if any. */
  solos: SoloRegion[];
}

export interface ClientSepOptions {
  /**
   * 'standard' — single pass + Wiener piano cleanup (default).
   * 'high'     — adds shift ensembling (2 passes, ~2× slower, demucs's
   *              reference quality trick; smooths chunk-boundary artifacts).
   * (Both apply to the demucs engine only.)
   */
  quality?: 'standard' | 'high';
  /** Which model to run. Default 'demucs'. 'roformer' = BS-RoFormer-SW. */
  engine?: SepEngine;
  /**
   * Abort the run. Purely additive — a caller that doesn't pass one behaves
   * exactly as before, which is why this could be added to a module the live
   * Studio also uses.
   *
   * Inference cannot be interrupted politely once it is running, so aborting
   * TERMINATES the worker and clears the module's cached handle. That means a
   * cancelled run genuinely stops burning CPU rather than being ignored, at the
   * cost of re-creating the session on the next run (the model bytes stay
   * cached, so it is a session rebuild, not a re-download).
   */
  signal?: AbortSignal;
}

/** Drop the worker and the cached readiness so the next run starts clean. */
function resetWorker(): void {
  if (worker) { try { worker.terminate(); } catch { /* already gone */ } }
  worker = null;
  workerEngine = null;
  readyPromise = null;
}

/** Separate stereo PCM into 6 stems; returns blob: URLs + which stems are ~silent. */
export async function separateClientSide(
  left: Float32Array, right: Float32Array, sampleRate: number, onProgress?: SepProgress,
  options?: ClientSepOptions,
): Promise<ClientSepResult> {
  const engine: SepEngine = options?.engine === 'roformer' ? 'roformer' : 'demucs';
  const signal = options?.signal;
  const aborted = () => new DOMException('Separation cancelled', 'AbortError');
  if (signal?.aborted) throw aborted();
  // Listen to warm-up progress (replays the current state if a preloadEngine()
  // warm is already mid-flight, so the bar doesn't restart from zero).
  attachProgress(onProgress ?? null);
  try {
    await ensureReady(engine);
  } finally {
    attachProgress(null);
  }
  // Cancelling during the engine warm-up is the common case on a cold visit —
  // that stretch is the long one. The download itself is resumable and its
  // bytes are already cached, so nothing is wasted by stopping here.
  if (signal?.aborted) { resetWorker(); throw aborted(); }
  const w = worker!;
  // Worker-side quality knobs (demucs engine). Wiener piano re-masking is
  // always on for demucs (validated +2.7 dB SI-SDR); 'high' adds shifts=2.
  // The roformer engine runs its reference pipeline as-is (no wiener/shifts —
  // its piano is already strong; unvalidated post-processing stays off).
  const sepOptions = engine === 'roformer'
    ? { wienerStems: [] }
    : {
        shifts: options?.quality === 'high' ? 2 : 0,
        overlap: 0.25,
        wienerStems: ['piano'],
      };
  const stems = await new Promise<Record<string, [Float32Array, Float32Array]>>((resolve, reject) => {
    const done = () => {
      w.removeEventListener('message', on);
      signal?.removeEventListener('abort', onAbort);
    };
    // Mid-inference the worker cannot answer a polite "stop", so it is killed
    // outright. `resetWorker` also clears the cached readiness — without that
    // the next run would hand out a dead worker and hang.
    const onAbort = () => { done(); resetWorker(); reject(aborted()); };
    const on = (e: MessageEvent) => {
      const m = e.data;
      if (m.type === 'progress') onProgress?.(0.7 + 0.29 * (m.total ? m.step / m.total : 0), 'Separating in browser');
      else if (m.type === 'done') { done(); resolve(m.stems); }
      else if (m.type === 'error') { done(); reject(new Error(m.error)); }
    };
    w.addEventListener('message', on);
    signal?.addEventListener('abort', onAbort, { once: true });
    w.postMessage({ type: 'separate', left: left.slice(0), right: right.slice(0), sampleRate, options: sepOptions });
  });

  const stemUrls = {} as Record<StemName, string>;
  const emptyStems = new Set<StemName>();
  for (const name of STEMS) {
    const pair = stems[name];
    if (!pair) { emptyStems.add(name); continue; }
    if (rms(pair[0]) < 0.0008 && rms(pair[1]) < 0.0008) emptyStems.add(name);
    stemUrls[name] = URL.createObjectURL(wavBlob(pair[0], pair[1], sampleRate));
  }
  // Auto-label the clip from the per-stem energies (cheap RMS pass on the PCM
  // we already have in hand) — "Guitar solo", "Drum solo", etc.
  let solos: SoloRegion[] = [];
  try { solos = detectSolos(stems as Partial<Record<StemName, [Float32Array, Float32Array]>>, sampleRate); } catch { /* non-fatal */ }
  onProgress?.(1, 'Done');
  return { stemUrls, emptyStems, solos };
}

/** Decode an uploaded file to 44.1 kHz stereo Float32 channels. */
export async function decodeFileToStereo(
  file: File,
): Promise<{ left: Float32Array; right: Float32Array; sampleRate: number; durationSec: number }> {
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ac = new AC({ sampleRate: 44100 });
  try {
    const buf = await ac.decodeAudioData(await file.arrayBuffer());
    const left = buf.getChannelData(0).slice(0);
    const right = buf.numberOfChannels > 1 ? buf.getChannelData(1).slice(0) : left.slice(0);
    return { left, right, sampleRate: buf.sampleRate, durationSec: buf.duration };
  } finally {
    try { ac.close(); } catch { /* ignore */ }
  }
}
